/**
 * AppShell — the design-system locked shell, refined to match skySign's AppLayout. Composition:
 *   <AppHeader/>            fixed DARK #242424 top panel (logo left; instance chip + profile right)
 *   <SkyNavDrawer/>         the ONLY nav — fixed below the header (top-12, h-[calc(100vh-3rem)], z-810),
 *                           grouped + lucide-iconed, one active item with the amber left-bar (size="medium")
 *   content offset          `pt-12 min-h-screen bg-surface-subtle md:ml-[260px]` (collapsed -> md:ml-16),
 *                           reacting to the drawer's onCollapseChange, holding:
 *     <DataHealthBanner/>   a SLIM provenance strip at the TOP of the content area (V1 honesty requirement)
 *     <main><Outlet/></main> every screen (its own PageHeader -> content -> PageFooter)
 *
 * Footer ownership: the shell does NOT render a footer; each page renders <PageFooter/> inside the Outlet
 * (per-page footer) to avoid a double footer.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { SkyNavDrawer, type NavSection } from "@skypointcloud/skypoint-ui";
import { Menu } from "lucide-react";
import { AppHeader } from "./AppHeader";
import { DataHealthBanner } from "./DataHealthBanner";
import { NAV_SECTIONS, routeToNavId, filterNavSections } from "./nav";
import { useSession } from "../lib/session";

/**
 * Adapter so SkyNavDrawer's `href`-based linkComponent drives react-router SPA navigation.
 * The drawer calls linkComponent with `{ href }`; react-router's Link wants `to` — map them.
 */
function RouterNavLink({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}

/**
 * M3 (mobile): below `md` the fixed desktop sidebar is hidden, so navigation is reached through a slim
 * top bar (hamburger) that opens the SAME SkyNavDrawer as an overlay over the content, mirroring the
 * skySign mobile pattern. The overlay closes on backdrop click, on Escape, on nav select, and on any
 * route change. Motion honors `prefers-reduced-motion` (200ms fade, disabled under reduce). Desktop is
 * unchanged: the whole block is `md:hidden`.
 *
 * A11y (WCAG 2.4.3 Focus Order / 2.1.2 No Keyboard Trap escape): the overlay is a modal dialog
 * (role="dialog" aria-modal="true"). On open, focus is moved INTO the drawer; while open, Tab/Shift+Tab
 * are trapped within the dialog and WRAP at both ends so keyboard focus can never escape behind the
 * backdrop; on close, focus is restored to the hamburger trigger so the keyboard user returns to where
 * they were. Escape still closes (the sanctioned escape hatch), so this is not a WCAG keyboard trap.
 */
function MobileNav({ pathname, sections }: { pathname: string; sections: NavSection[] }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close the overlay whenever the route changes (a nav link was followed).
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Escape closes the overlay (keyboard accessibility) while it is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus management + focus trap. On open: move focus into the dialog (first focusable, e.g. the
  // Close/backdrop button). While open: keep Tab/Shift+Tab cycling within the dialog, wrapping at both
  // ends. On close: restore focus to the hamburger trigger. Guarded for jsdom (focus() is a no-op there
  // for layout but activeElement still tracks, which the test relies on).
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );

    // Move focus into the drawer on open.
    const focusables = getFocusable();
    (focusables[0] ?? dialog).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const items = getFocusable();
      if (items.length === 0) {
        // Nothing focusable inside — keep focus pinned to the dialog itself.
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        // Shift+Tab off the first element (or from outside the dialog) wraps to the last.
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab off the last element (or from outside the dialog) wraps to the first.
        if (active === last || !dialog.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    dialog.addEventListener("keydown", onKeyDown);
    return () => {
      dialog.removeEventListener("keydown", onKeyDown);
      // Restore focus to the trigger when the dialog closes/unmounts.
      triggerRef.current?.focus();
    };
  }, [open]);

  return (
    <div className="md:hidden">
      {/* Slim mobile bar (below the fixed header): hamburger + product name. */}
      <div className="sticky top-12 z-[800] flex items-center gap-2 border-b border-neutral-200 bg-white px-4 py-2">
        <button
          ref={triggerRef}
          type="button"
          aria-label="Open navigation"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center rounded-md p-1.5 text-neutral-700 hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-80)]"
        >
          <Menu size={20} aria-hidden />
        </button>
        <span className="text-sm font-semibold text-neutral-800">skyCare</span>
      </div>

      {open && (
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Navigation" tabIndex={-1}>
          {/* Backdrop doubles as the close affordance (click outside to dismiss). */}
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setOpen(false)}
            className="fixed inset-0 top-12 z-[820] bg-black/40 transition-opacity duration-200 motion-reduce:transition-none"
          />
          <SkyNavDrawer
            sections={sections}
            selectedId={routeToNavId(pathname)}
            size="medium"
            mobileBreakpoint={0}
            showCopyright
            linkComponent={RouterNavLink}
            onSelect={() => setOpen(false)}
            className="fixed! left-0! top-12! z-[830]! h-[calc(100vh-3rem)]! shadow-xl!"
          />
        </div>
      )}
    </div>
  );
}

/**
 * P0-1 (a11y, WCAG 2.4.1 / 2.4.3) — one shared SPA route-announcer that lives in the shell (not per page,
 * SOLID single-responsibility). On every route change it:
 *   - announces the new page title through a persistent aria-live="polite" / role="status" region so
 *     screen-reader users hear the page change (an SPA does not reload, so nothing is announced otherwise);
 *   - moves keyboard/AT focus to the main content region (id=main-content, tabIndex=-1) so the next Tab
 *     starts inside the new page, not back at the top of the sidebar.
 * The very first render is announce-only (no focus steal) so the skip-link stays the first focusable on
 * initial load. The page title is read from the live H1 the page renders (PageHeader -> SkyTitle1 as=h1),
 * with a safe non-PHI fallback; the announcer never logs or renders row data.
 */
function RouteAnnouncer() {
  const { pathname } = useLocation();
  const [message, setMessage] = useState("");
  const firstRender = useRef(true);

  useEffect(() => {
    // Defer to the next frame so the freshly-navigated page (and its H1) has rendered before we read it.
    const raf = requestAnimationFrame(() => {
      const main = document.getElementById("main-content");
      const heading = main?.querySelector("h1")?.textContent?.trim();
      const title = heading && heading.length > 0 ? heading : "skyCare";
      setMessage(`Navigated to ${title}`);
      if (firstRender.current) {
        firstRender.current = false;
        return; // announce-only on first load; do not pull focus off the skip-link
      }
      main?.focus({ preventScroll: false });
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  return (
    <div aria-live="polite" role="status" className="sr-only" data-testid="route-announcer">
      {message}
    </div>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  // RBAC nav gating: filter the locked nav to the principal's allowed items. Until
  // the session loads (allowedNav null), the full nav renders (gating only tightens).
  const { allowedNav } = useSession();
  const sections = filterNavSections(NAV_SECTIONS, allowedNav);

  // M3: DESKTOP-FIRST. The fixed sidebar is hidden below `md` and the content offset only applies at
  // `md+`, so on small screens the content is full-width and the sidebar does not overlap. Below `md`,
  // navigation is reached through the <MobileNav> hamburger + overlay drawer (skySign mobile pattern).
  return (
    <div className="min-h-screen bg-surface-subtle text-neutral-900">
      {/* P0-1 (WCAG 2.4.1 Bypass Blocks): the FIRST focusable element is a visually-hidden skip link that
          becomes visible on focus and jumps past the header + sidebar straight to #main-content. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-2 focus:z-[900] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-neutral-900 focus:shadow-lg focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-brand-80)]"
      >
        Skip to main content
      </a>

      {/* P0-1: shared SPA route-announcer (aria-live + route-change focus management) for the whole app. */}
      <RouteAnnouncer />

      {/* Fixed dark top panel (logo left; chip + profile right). */}
      <AppHeader />

      {/* The ONLY nav: fixed below the header, grouped + iconed (skySign SkyNavDrawer props).
          M3: `hidden md:block` — desktop-first; the fixed sidebar is not rendered below md. */}
      <SkyNavDrawer
        sections={sections}
        selectedId={routeToNavId(pathname)}
        size="medium"
        collapsible
        onCollapseChange={setCollapsed}
        showCopyright
        linkComponent={RouterNavLink}
        className="hidden! fixed! left-0! top-12! z-[810]! h-[calc(100vh-3rem)]! md:block!"
      />

      {/* Content: neutral page bg, offset under the fixed header + sidebar; reacts to collapse.
          M3: the ml offset is md-only, so content is full-width below md (no sidebar overlap). */}
      <div
        className={`min-h-screen bg-surface-subtle pt-12 transition-[margin] duration-200 ${
          collapsed ? "md:ml-16!" : "md:ml-[260px]!"
        }`}
      >
        {/* Mobile-only nav: hamburger bar + overlay drawer (md:hidden); desktop uses the fixed sidebar. */}
        <MobileNav pathname={pathname} sections={sections} />
        {/* SLIM provenance strip at the top of the content area (below header, right of sidebar).
            It renders ABOVE the per-route boundary, so a crash here would otherwise propagate to the
            ROOT boundary and unmount the ENTIRE shell. Contain it in a `chrome` boundary: a shell-strip
            crash degrades to a minimal inline notice and never takes down the header, nav, or content. */}
        <ErrorBoundary variant="chrome" boundaryName="data-health-banner">
          <DataHealthBanner />
        </ErrorBoundary>
        {/* P0-1: the labelled main landmark (id=main-content) is the skip-link + route-announcer focus
            target. tabIndex=-1 makes it programmatically focusable without entering the tab order. */}
        <main
          id="main-content"
          tabIndex={-1}
          role="main"
          aria-label="Main content"
          className="flex flex-col px-8 py-6 outline-none"
        >
          {/* Per-route crash containment: a render throw in ONE page shows a contained fallback and
              keeps the shell (header + nav) mounted; keyed on pathname so navigating away auto-clears. */}
          <ErrorBoundary variant="route" resetKey={pathname} boundaryName="route">
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
